import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const fmt = (d) => d ? String(d).slice(0, 10) : '';

const th = { padding: '0.5rem 0.75rem', background: 'linear-gradient(135deg,#D84315,#F57C00)', color: '#fff', fontWeight: 600, fontSize: '0.8rem', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { padding: '0.45rem 0.75rem', fontSize: '0.875rem', borderBottom: '1px solid #e0e0e0', color: '#2c3e50' };
const inp = { padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: 5, fontSize: '0.875rem' };
const btn = (variant = 'primary') => ({
  padding: '0.4rem 0.9rem', border: 'none', borderRadius: 5, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
  background: variant === 'primary' ? 'linear-gradient(135deg,#D84315,#F57C00)' : variant === 'secondary' ? '#e0e0e0' : variant === 'success' ? '#27ae60' : '#17a2b8',
  color: variant === 'secondary' ? '#333' : '#fff',
});
const label = { fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: 3, display: 'block' };
const section = { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '1.1rem', marginBottom: '1.25rem' };

export default function CreateInvoice() {
  const { user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${user?.token}` };
  const navigate = useNavigate();

  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [invoiceDate, setDate]    = useState('');
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines]   = useState([]);
  const [channels, setChannels]   = useState([]);
  const [orders, setOrders]       = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('');

  const [searchForm, setSearchForm] = useState({ order_no: '', from_date: '', to_date: '', customer_id: '', subsidiary: '' });

  const [uninvFilters, setUninvFilters]   = useState({ from: '', to: '', sales_channel_id: '', customer_name: '' });
  const [uninvResults, setUninvResults]   = useState([]);
  const [uninvSelected, setUninvSelected] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/commonLOVs/lhcustomers`, { headers }).then(r => setCustomers(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/commonLOVs/lhvendingmachines`, { headers }).then(r => setMachines(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/commonLOVs/saleschannels`, { headers }).then(r => setChannels(r.data || [])).catch(() => {});
  }, []);

  const fetchOrders = async () => {
    const { order_no, from_date, to_date } = searchForm;
    if (!order_no?.trim() && !(from_date && to_date)) { alert('Enter Order No OR From/To dates.'); return; }
    try {
      const res = await axios.get(`${API_URL}/invoices/orders?${new URLSearchParams(searchForm)}`, { headers });
      const data = res.data || [];
      setOrders(data);
      setSelectedOrders(data.map(o => o.id));
    } catch { alert('Failed to fetch orders.'); }
  };

  const resetSearch = () => {
    setSearchForm({ order_no: '', from_date: '', to_date: '', customer_id: '', subsidiary: '' });
    setOrders([]);
    setSelectedOrders([]);
  };

  const toggleOrder = (id) => setSelectedOrders(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const createInvoice = async (ids) => {
    if (!invoiceDate || ids.length === 0) { alert('Select invoice date and at least one order.'); return; }
    setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/invoices/invoices`, { invoice_date: invoiceDate, order_ids: ids }, { headers });
      setSuccess(`Invoice created: ${res.data.invoice_no}`);
      setOrders([]); setSelectedOrders([]);
      setUninvResults([]); setUninvSelected([]);
    } catch (err) {
      const d = err.response?.data;
      if (d?.error_code === 'DUPLICATE_ORDER') {
        setError(`Already invoiced: ${(d.duplicate_orders || []).join(', ')}`);
      } else {
        setError(d?.error || 'Failed to create invoice.');
      }
    }
  };

  const createVmInvoice = async () => {
    if (!invoiceDate || !selectedMachine) { alert('Select a machine and invoice date.'); return; }
    setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/invoices/create-vm-monthly-invoice`, { machine_id: selectedMachine, invoice_date: invoiceDate }, { headers });
      setSuccess(res.data.message || 'VM invoice created.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create VM invoice.');
    }
  };

  const fetchUninvoiced = async () => {
    try {
      const res = await axios.get(`${API_URL}/invoices/uninvoiced-orders?${new URLSearchParams(uninvFilters)}`, { headers });
      const data = res.data || [];
      setUninvResults(data);
      setUninvSelected(data.map(o => o.id));
    } catch { setError('Failed to load uninvoiced orders.'); }
  };

  const toggleUninv = (id) => setUninvSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllUninv = (e) => setUninvSelected(e.target.checked ? uninvResults.map(o => o.id) : []);

  return (
    <div style={{ padding: '1.25rem', maxWidth: 900 }}>
      <button style={{ ...btn('secondary'), marginBottom: '1rem' }} onClick={() => navigate(-1)}>← Back</button>

      {error   && <div style={{ background: '#fee', color: '#c33', border: '1px solid #fcc', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem' }}>{error}</div>}
      {success && <div style={{ background: '#efe', color: '#2a7a2a', border: '1px solid #cfc', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem' }}>{success}</div>}

      <h2 style={{ margin: '0 0 1.25rem', fontSize: '1.2rem', color: '#2c3e50' }}>Create Invoice</h2>

      {/* Invoice date — shared */}
      <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#333' }}>Invoice Date</span>
        <input type="date" style={inp} value={invoiceDate} onChange={e => setDate(e.target.value)} />
      </div>

      {/* Order search */}
      <div style={section}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600 }}>Search Orders</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
          <div><label style={label}>Order No</label><input style={inp} placeholder="Order No" value={searchForm.order_no} onChange={e => setSearchForm(p => ({ ...p, order_no: e.target.value }))} /></div>
          <div><label style={label}>From</label><input type="date" style={inp} value={searchForm.from_date} onChange={e => setSearchForm(p => ({ ...p, from_date: e.target.value }))} /></div>
          <div><label style={label}>To</label><input type="date" style={inp} value={searchForm.to_date} onChange={e => setSearchForm(p => ({ ...p, to_date: e.target.value }))} /></div>
          <div>
            <label style={label}>Subsidiary</label>
            <select style={inp} value={searchForm.subsidiary} onChange={e => setSearchForm(p => ({ ...p, subsidiary: e.target.value }))}>
              <option value="">All</option>
              <option value="LH">Local Heros</option>
              <option value="FRB">Frootbar</option>
            </select>
          </div>
          <div>
            <label style={label}>Customer</label>
            <select style={inp} value={searchForm.customer_id} onChange={e => setSearchForm(p => ({ ...p, customer_id: e.target.value }))}>
              <option value="">All</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.customer_name}</option>)}
            </select>
          </div>
          <button style={btn()} onClick={fetchOrders}>Search</button>
          <button style={btn('secondary')} onClick={resetSearch}>Reset</button>
        </div>

        {orders.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Select</th>
                    <th style={th}>Order No</th>
                    <th style={th}>Order Date</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={selectedOrders.includes(o.id)} onChange={() => toggleOrder(o.id)} /></td>
                      <td style={td}>{o.order_no}</td>
                      <td style={td}>{fmt(o.order_date)}</td>
                      <td style={td}>{o.customer_name}</td>
                      <td style={td}>{o.receipt_amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button style={{ ...btn('success'), marginTop: '0.75rem' }} onClick={() => createInvoice(selectedOrders)}>Create Invoice</button>
          </>
        )}
      </div>

      {/* VM Invoice */}
      <div style={section}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600 }}>Vending Machine Invoice</h3>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select style={inp} value={selectedMachine} onChange={e => setSelectedMachine(e.target.value)}>
            <option value="">-- Select Machine --</option>
            {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          {selectedMachine && invoiceDate && (
            <button style={btn('success')} onClick={createVmInvoice}>Create Invoice</button>
          )}
        </div>
      </div>

      {/* Uninvoiced Orders */}
      <div style={section}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600 }}>Uninvoiced Orders</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
          <div><label style={label}>From</label><input type="date" style={inp} value={uninvFilters.from} onChange={e => setUninvFilters(p => ({ ...p, from: e.target.value }))} /></div>
          <div><label style={label}>To</label><input type="date" style={inp} value={uninvFilters.to} onChange={e => setUninvFilters(p => ({ ...p, to: e.target.value }))} /></div>
          <div>
            <label style={label}>Sales Channel</label>
            <select style={inp} value={uninvFilters.sales_channel_id} onChange={e => setUninvFilters(p => ({ ...p, sales_channel_id: e.target.value }))}>
              <option value="">All</option>
              {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label style={label}>Customer Name</label><input style={inp} placeholder="Customer Name" value={uninvFilters.customer_name} onChange={e => setUninvFilters(p => ({ ...p, customer_name: e.target.value }))} /></div>
          <button style={{ ...btn('info'), alignSelf: 'flex-end' }} onClick={fetchUninvoiced}>Fetch</button>
        </div>

        {uninvResults.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'center' }}><input type="checkbox" checked={uninvSelected.length === uninvResults.length} onChange={toggleAllUninv} /></th>
                    <th style={th}>Order No</th>
                    <th style={th}>Order Date</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {uninvResults.map(o => (
                    <tr key={o.id}>
                      <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={uninvSelected.includes(o.id)} onChange={() => toggleUninv(o.id)} /></td>
                      <td style={td}>{o.order_no}</td>
                      <td style={td}>{fmt(o.order_date)}</td>
                      <td style={td}>{o.customer_name}</td>
                      <td style={td}>{o.receipt_amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button style={{ ...btn('success'), marginTop: '0.75rem' }} onClick={() => createInvoice(uninvSelected)}>Create Invoice</button>
          </>
        )}
      </div>
    </div>
  );
}
